/*
 * component-product-tabs.js — Slice 20 Phase B + UNIT 2.
 *
 * Shared product-tabs engine (horizontal tabs / accordion / vertical rail).
 * Parent-agnostic: keys on data-attributes only, so the same engine serves
 * sections/product-tabs.liquid + the info-column _product-tabs / _product-accordion
 * blocks. Structure mirrors section-faq.js.
 *
 * Behaviors:
 *   1. generateTabs(root) — scan [data-ptab-panel] divs and build one
 *      role="tab" <button> per panel into [data-ptab-list] (after the
 *      indicator, which stays first child). Wires id <-> aria-controls /
 *      aria-labelledby. Marks the first tab + panel active, then adds
 *      `is-ready` to the root so the CSS switches from the no-JS
 *      sibling-combinator fallback to active-driven panel display.
 *   2. activateTab(root, btn) — roving tabindex across tabs; show the matching
 *      panel, hide siblings (the class toggle re-runs the panel fade because
 *      the panel goes display:none -> block); reposition the indicator.
 *   3. positionIndicator(root) — translateX + width the sliding indicator to
 *      the active tab (no-op visually for the pills design, which hides it).
 *   4. setupTabArrows(root) — ported from section-faq.js: hide each scroll
 *      arrow at its boundary (and both when the strip does not overflow);
 *      smooth-scroll ~70% of the strip width per tap. Overflow-driven at any
 *      breakpoint (the CSS reveal is NOT gated to mobile).
 *   5. Keyboard a11y — document-level keydown on the tablist: ArrowLeft/Right
 *      move + activate (wrap), Home/End jump to first/last. Enter/Space is the
 *      native button click (covered by the delegated click).
 *   6. resizeObserve(root) — a single ResizeObserver on [data-ptab-list]
 *      repositions the indicator + refreshes the arrows on layout change;
 *      window-resize fallback when ResizeObserver is absent. Also re-checks
 *      read-more on the active panel (rAF-wrapped) on either path.
 *   7. checkReadMore / toggleReadMore — read-more / collapse (Phase B2). On a
 *      VISIBLE panel only, measure content overflow against the inherited
 *      --ptab-collapse-height threshold; when it overflows, inject a single
 *      toggle button (panel's last child, sibling of the clamp wrapper) and
 *      collapse by default. The user's manual toggle is remembered per panel
 *      via the __ptabRmManual expando so a resize re-check never stomps it.
 *      Feature is fully off when the threshold is 0 / absent. Re-measured on
 *      tab show (initRoot + activateTab) and on resize.
 *   8. Accordion mode (Slice 20 Phase C) — when the root carries
 *      data-ptab-layout="accordion", initRoot branches to initAccordion
 *      instead of the tabs init: each panel gets a JS-built STRUCTURED trigger
 *      (UNIT 1 — optional leading icon promoted from the server-rendered hidden
 *      .product-tabs__acc-icon-src, then __acc-text{__acc-title + optional
 *      __acc-subtitle from data-ptab-subtitle}, then the CSS ::after indicator) +
 *      runtime role="region"/aria-labelledby (the block's tabpanel role +
 *      tabindex are neutralized), and collapses independently via an inline
 *      max-block-size set to the content scrollHeight on open / 0 on close
 *      (re-measured each open; no transitionend — matches the Slice-18 shared
 *      accordion). Optional single-open + open-first via data-attrs. The ONE
 *      delegated click now handles tabs + read-more + accordion triggers.
 *   9. Vertical rail (Slice 20 UNIT 2 Checkpoint B) — when the root carries
 *      data-ptab-layout="vertical", initRoot branches to initVertical, which
 *      REUSES the full horizontal path (generateTabs + activateTab +
 *      setupTabArrows + resizeObserve + read-more) and only adds optional icon
 *      promotion (promoteTabIcon, from the child's hidden
 *      .product-tabs__acc-icon-src) + rail-head population (updateRailHead —
 *      mirrors the active panel's title/subtitle into the content-column header).
 *      The rail vs horizontal-strip split is PURE CSS gated to min-width:750px,
 *      so ≤749px degrades to the horizontal strip in the chosen tab_design with
 *      no extra JS. updateRailHead is also appended to activateTab (the ONLY
 *      shared-function edit) and no-ops on the 4 non-vertical hosts via its guard.
 *
 * Single document-level click (event delegation) + single document-level
 * keydown. Per-root __ptabInited guard; per-panel __ptabRmManual expando.
 * Storefront variant-swap re-init (Slice 20 Phase D): a document-level
 * `product:variant-changed` listener calls initAll(detail.root) so info-column
 * _product-tabs / _product-accordion blocks rebuild after the product info
 * column's SRA innerHTML swap. Inert for the standalone section, which renders
 * outside [data-product-info-root] (initAll(detail.root) never finds it).
 * ES5-style (var, function declarations).
 *
 * Rules in force:
 *   Rule J1  — addEventListener only
 *   Rule J7  — document-level delegated listeners
 *   Rule J8  — no window.<theme> namespace pollution; pure IIFE
 *   Rule J12 — IIFE-dedup guard for multi-instance safety
 */
(function () {
  if (window.__BLOCKDEDUP_PRODUCT_TABS__) return;
  window.__BLOCKDEDUP_PRODUCT_TABS__ = true;

  /* ---------- Tab generation ---------- */

  function generateTabs(root) {
    var list = root.querySelector('[data-ptab-list]');
    if (!list) return;
    var panels = root.querySelectorAll('[data-ptab-panel]');
    if (!panels.length) return;

    var sectionId = root.getAttribute('data-section-id') || 'pt';

    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];
      var title = panel.getAttribute('data-ptab-title') || ('Tab ' + (i + 1));

      var panelId = panel.id;
      if (!panelId) {
        panelId = 'ptab-panel-' + sectionId + '-' + i;
        panel.id = panelId;
      }
      var tabId = 'ptab-tab-' + sectionId + '-' + i;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-tabs__tab';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('data-ptab', '');
      btn.id = tabId;
      btn.setAttribute('aria-controls', panelId);
      btn.textContent = title;

      panel.setAttribute('aria-labelledby', tabId);

      if (i === 0) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');
        panel.classList.add('is-active');
      } else {
        btn.setAttribute('aria-selected', 'false');
        btn.setAttribute('tabindex', '-1');
        panel.classList.remove('is-active');
      }

      list.appendChild(btn);
    }

    root.classList.add('is-ready');
  }

  /* ---------- Tab activation ---------- */

  function activateTab(root, btn) {
    var targetId = btn.getAttribute('aria-controls');

    var tabs = root.querySelectorAll('[data-ptab]');
    for (var i = 0; i < tabs.length; i++) {
      var t = tabs[i];
      if (t === btn) {
        t.classList.add('is-active');
        t.setAttribute('aria-selected', 'true');
        t.setAttribute('tabindex', '0');
      } else {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      }
    }

    var panels = root.querySelectorAll('[data-ptab-panel]');
    for (var j = 0; j < panels.length; j++) {
      var p = panels[j];
      if (p.id === targetId) {
        p.classList.add('is-active');
      } else {
        p.classList.remove('is-active');
      }
    }

    positionIndicator(root);

    var newPanel = root.querySelector('[data-ptab-panel].is-active');
    if (newPanel) checkReadMore(newPanel);

    updateRailHead(root);   // vertical-rail head sync; no-ops on the 4 non-vertical hosts
  }

  /* ---------- Read-more / collapse (Phase B2) ---------- */

  /* Measure a VISIBLE panel's content against the inherited collapse-height
     threshold and manage the clamp + toggle button. No-op when the feature is
     off (threshold 0 / absent) — backward-compatible default. */
  function checkReadMore(panel) {
    var content = panel.querySelector('.product-tabs__panel-content');
    if (!content) return;

    var threshold = parseInt(getComputedStyle(panel).getPropertyValue('--ptab-collapse-height'), 10);

    var btn = panel.querySelector('[data-ptab-readmore]');

    // Feature off for this panel: drop any clamp + hide any button.
    if (!threshold || threshold <= 0) {
      panel.classList.remove('is-clamped');
      if (btn) btn.hidden = true;
      return;
    }

    var overflows = content.scrollHeight > threshold + 4;

    if (overflows) {
      var root = panel.closest('[data-product-tabs]');
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-tabs__readmore';
        btn.setAttribute('data-ptab-readmore', '');
        panel.appendChild(btn);
      }
      btn.hidden = false;

      // a11y: associate the toggle with the region it clamps (read-more §9).
      // The clamped element is .product-tabs__panel-content; give it a stable
      // id derived from the panel id when it has none, then point aria-controls.
      if (!content.id) content.id = (panel.id || 'ptab-panel') + '-content';
      btn.setAttribute('aria-controls', content.id);

      // Apply the default collapsed state unless the user has toggled this panel.
      if (!panel.__ptabRmManual) {
        panel.classList.add('is-clamped');
        btn.setAttribute('aria-expanded', 'false');
      }
      var moreLabel = (root && root.getAttribute('data-ptab-more-label')) || 'Lire la suite';
      var lessLabel = (root && root.getAttribute('data-ptab-less-label')) || 'Voir moins';
      btn.textContent = panel.classList.contains('is-clamped') ? moreLabel : lessLabel;
    } else {
      // No overflow ⇒ no read-more (ignores the manual flag, by design).
      panel.classList.remove('is-clamped');
      if (btn) btn.hidden = true;
    }
  }

  function toggleReadMore(btn) {
    var panel = btn.closest('[data-ptab-panel]');
    if (!panel) return;
    var root = panel.closest('[data-product-tabs]');

    var clamped = panel.classList.toggle('is-clamped');
    btn.setAttribute('aria-expanded', clamped ? 'false' : 'true');

    var moreLabel = (root && root.getAttribute('data-ptab-more-label')) || 'Lire la suite';
    var lessLabel = (root && root.getAttribute('data-ptab-less-label')) || 'Voir moins';
    btn.textContent = clamped ? moreLabel : lessLabel;

    panel.__ptabRmManual = true;
  }

  /* ---------- Sliding indicator ---------- */

  function positionIndicator(root) {
    var list = root.querySelector('[data-ptab-list]');
    var indicator = root.querySelector('[data-ptab-indicator]');
    if (!list || !indicator) return;
    var active = list.querySelector('[data-ptab].is-active');
    if (!active) return;
    indicator.style.transform = 'translateX(' + active.offsetLeft + 'px)';
    indicator.style.width = active.offsetWidth + 'px';
  }

  /* ---------- Scroll arrows (ported from section-faq.js) ---------- */

  function setupTabArrows(root) {
    var wrap = root.querySelector('[data-ptab-wrap]');
    if (!wrap) return;
    var strip = wrap.querySelector('[data-ptab-list]');
    var prev = wrap.querySelector('[data-ptab-prev]');
    var next = wrap.querySelector('[data-ptab-next]');
    if (!strip || !prev || !next) return;

    function update() {
      var maxScroll = strip.scrollWidth - strip.clientWidth;
      if (maxScroll <= 1) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }
      prev.hidden = strip.scrollLeft <= 1;
      next.hidden = strip.scrollLeft >= maxScroll - 1;
    }

    function scrollByAmount(dir) {
      var amount = Math.max(strip.clientWidth * 0.7, 100);
      strip.scrollBy({ left: dir * amount, behavior: 'smooth' });
    }

    prev.addEventListener('click', function () { scrollByAmount(-1); });
    next.addEventListener('click', function () { scrollByAmount(1); });
    strip.addEventListener('scroll', function () {
      window.requestAnimationFrame(update);
    });
    window.addEventListener('resize', function () {
      window.requestAnimationFrame(update);
    });
    update();

    root.__ptabArrowsUpdate = update;
  }

  /* ---------- Keyboard a11y ---------- */

  function onKeydown(e) {
    var list = e.target.closest ? e.target.closest('[data-ptab-list]') : null;
    if (!list) return;
    var current = e.target.closest('[data-ptab]');
    if (!current) return;
    var root = e.target.closest('[data-product-tabs]');
    if (!root) return;

    var tabs = list.querySelectorAll('[data-ptab]');
    if (!tabs.length) return;

    var idx = -1;
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i] === current) { idx = i; break; }
    }
    if (idx === -1) return;

    // ArrowUp/Down mirror ArrowLeft/Right so the vertical rail is keyboard-
    // operable on its visual axis (APG tabs; harmless for horizontal tabs).
    var target = null;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':  target = tabs[(idx + 1) % tabs.length]; break;
      case 'ArrowLeft':
      case 'ArrowUp':    target = tabs[(idx - 1 + tabs.length) % tabs.length]; break;
      case 'Home':       target = tabs[0]; break;
      case 'End':        target = tabs[tabs.length - 1]; break;
      default: return;
    }

    e.preventDefault();
    activateTab(root, target);
    target.focus();
  }

  /* ---------- Resize tracking ---------- */

  function resizeObserve(root) {
    var list = root.querySelector('[data-ptab-list]');
    if (!list) return;

    if (typeof window.ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () {
        positionIndicator(root);
        if (root.__ptabArrowsUpdate) root.__ptabArrowsUpdate();
        var activePanel = root.querySelector('[data-ptab-panel].is-active');
        if (activePanel) window.requestAnimationFrame(function () { checkReadMore(activePanel); });
      });
      ro.observe(list);
    } else {
      window.addEventListener('resize', function () {
        window.requestAnimationFrame(function () {
          positionIndicator(root);
          var activePanel = root.querySelector('[data-ptab-panel].is-active');
          if (activePanel) checkReadMore(activePanel);
        });
      });
    }
  }

  /* ---------- Accordion layout (Slice 20 Phase C) ---------- */

  /* Open/close one panel. Shared by init (open-first), the click toggle, and
     the single-open close-others path. On open the `.is-open` class is added
     BEFORE measuring scrollHeight so the open-state region padding is included
     in the measured height; the inline max-block-size then drives the CSS
     transition. On close it returns to 0. No transitionend (Slice-18 parity). */
  function setPanelOpen(panel, open) {
    var trigger = panel.querySelector('[data-ptab-acc-trigger]');
    var content = panel.querySelector('.product-tabs__panel-content');
    if (open) {
      panel.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (content) {
        content.style.maxBlockSize = content.scrollHeight + 'px';
        // GLOW 6.0.3 fix : la hauteur mesuree a l'ouverture (souvent au chargement, avant
        // l'arrivee des polices et CSS differes) coupait la fin du texte (ex. paragraphe
        // "Utilisation" invisible). Une fois l'animation finie, on libere la hauteur.
        clearTimeout(content._ptabFree);
        content._ptabFree = setTimeout(function () {
          if (panel.classList.contains('is-open')) content.style.maxBlockSize = 'none';
        }, 450);
      }
    } else {
      panel.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (content) {
        clearTimeout(content._ptabFree);
        if (content.style.maxBlockSize === 'none') {
          // repartir d'une hauteur chiffree pour garder l'animation de fermeture
          content.style.maxBlockSize = content.scrollHeight + 'px';
          void content.offsetHeight;
        }
        content.style.maxBlockSize = '0';
      }
    }
  }

  function initAccordion(root) {
    var panels = root.querySelectorAll('[data-ptab-panel]');
    if (!panels.length) return;

    var sectionId = root.getAttribute('data-section-id') || 'pt';
    var openFirst = root.getAttribute('data-ptab-acc-open-first') !== 'false';

    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];
      var content = panel.querySelector('.product-tabs__panel-content');
      var title = panel.getAttribute('data-ptab-title') || ('Section ' + (i + 1));

      var contentId = '';
      if (content) {
        contentId = content.id;
        if (!contentId) {
          contentId = 'ptab-panel-' + sectionId + '-' + i;
          content.id = contentId;
        }
      }
      var triggerId = 'ptab-acc-trigger-' + sectionId + '-' + i;

      // Neutralize the block's tabs-mode a11y — the trigger is the control.
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-labelledby', triggerId);
      panel.removeAttribute('tabindex');

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'product-tabs__acc-trigger';
      trigger.setAttribute('data-ptab-acc-trigger', '');
      trigger.id = triggerId;
      if (contentId) trigger.setAttribute('aria-controls', contentId);

      // Structured trigger (mirrors the Slice-18 FAQ header order: icon →
      // text{title + optional subtitle} → the CSS ::after indicator). The
      // server renders the glyph into a hidden .product-tabs__acc-icon-src as
      // the panel's first child (UNIT 1 §1A); promote it here, then drop the
      // source wrapper. textContent only for user copy (no innerHTML).
      var iconSrc = panel.querySelector('.product-tabs__acc-icon-src');
      if (iconSrc) {
        var svg = iconSrc.querySelector('svg');
        if (svg) {
          var iconWrap = document.createElement('span');
          iconWrap.className = 'product-tabs__acc-icon';
          iconWrap.setAttribute('aria-hidden', 'true');
          iconWrap.appendChild(svg);
          trigger.appendChild(iconWrap);
        }
        if (iconSrc.parentNode) iconSrc.parentNode.removeChild(iconSrc);
      }

      var textWrap = document.createElement('span');
      textWrap.className = 'product-tabs__acc-text';
      var titleEl = document.createElement('span');
      titleEl.className = 'product-tabs__acc-title';
      titleEl.textContent = title;
      textWrap.appendChild(titleEl);

      var subtitle = panel.getAttribute('data-ptab-subtitle');
      if (subtitle) {
        var subEl = document.createElement('span');
        subEl.className = 'product-tabs__acc-subtitle';
        subEl.textContent = subtitle;
        textWrap.appendChild(subEl);
      }
      trigger.appendChild(textWrap);

      panel.insertBefore(trigger, panel.firstChild);

      setPanelOpen(panel, openFirst && i === 0);
    }

    root.classList.add('is-ready');
  }

  function toggleAccordion(trigger) {
    var panel = trigger.closest('[data-ptab-panel]');
    if (!panel) return;
    var root = panel.closest('[data-product-tabs]');
    if (!root) return;

    var willOpen = !panel.classList.contains('is-open');

    if (willOpen && root.hasAttribute('data-ptab-acc-single')) {
      var openPanels = root.querySelectorAll('[data-ptab-panel].is-open');
      for (var i = 0; i < openPanels.length; i++) {
        if (openPanels[i] !== panel) setPanelOpen(openPanels[i], false);
      }
    }

    setPanelOpen(panel, willOpen);
  }

  /* ---------- Vertical rail (Slice 20 UNIT 2 Checkpoint B) ---------- */

  /* Promote a panel's server-rendered hidden icon source into its rail tab
     button — the SAME promote-from-hidden mechanism as initAccordion. Vertical-
     only: a panel is in exactly one layout per root, so the .product-tabs__acc-
     icon-src is consumed here OR by initAccordion, never both (no double-consume). */
  function promoteTabIcon(tab, panel) {
    var src = panel.querySelector('.product-tabs__acc-icon-src');
    if (!src) return;
    var svg = src.querySelector('svg');
    if (svg) {
      var w = document.createElement('span');
      w.className = 'product-tabs__tab-icon';
      w.setAttribute('aria-hidden', 'true');
      w.appendChild(svg);
      tab.insertBefore(w, tab.firstChild);   // before the textContent title node
    }
    if (src.parentNode) src.parentNode.removeChild(src);
  }

  /* Mirror the active panel's title + subtitle into the rail-head (the content
     column header). Guarded to vertical layout so it is a safe no-op when called
     unconditionally from activateTab on the 4 non-vertical hosts. The rail-head
     spans are server-rendered empty; this fills them (no FOUC — CSS hides the
     rail-head until .is-ready). textContent only (no innerHTML for user copy). */
  function updateRailHead(root) {
    if (root.getAttribute('data-ptab-layout') !== 'vertical') return;
    var head = root.querySelector('[data-ptab-rail-head]');
    if (!head) return;
    var panel = root.querySelector('[data-ptab-panel].is-active');
    if (!panel) return;
    var t = head.querySelector('.product-tabs__rail-title');
    var s = head.querySelector('.product-tabs__rail-sub');
    if (t) t.textContent = panel.getAttribute('data-ptab-title') || '';
    var sub = panel.getAttribute('data-ptab-subtitle') || '';
    if (s) { s.textContent = sub; s.hidden = !sub; }
  }

  /* Vertical rail init — REUSES the full horizontal engine (generateTabs +
     activateTab + setupTabArrows + resizeObserve + positionIndicator + read-more)
     untouched. The rail vs horizontal-strip distinction is PURE CSS: the grid +
     per-item active indicator are gated to @media (min-width:750px), so ≤749px
     degrades to the horizontal tab strip in the chosen tab_design with zero extra
     JS. Only additions over the horizontal path: optional icon promotion + the
     rail-head population. positionIndicator runs harmlessly (the sliding indicator
     is display:none ≥750px). */
  function initVertical(root) {
    generateTabs(root);
    // a11y: the rail is a vertical tablist (APG). Horizontal tabs keep the
    // default (horizontal) orientation; this is the vertical-only override.
    var vlist = root.querySelector('[data-ptab-list]');
    if (vlist) vlist.setAttribute('aria-orientation', 'vertical');
    if (root.getAttribute('data-ptab-rail-icons') === 'true') {
      var tabs = root.querySelectorAll('[data-ptab]');
      for (var i = 0; i < tabs.length; i++) {
        var pid = tabs[i].getAttribute('aria-controls');
        var p = pid ? document.getElementById(pid) : null;
        if (p) promoteTabIcon(tabs[i], p);
      }
    }
    setupTabArrows(root);
    resizeObserve(root);
    window.requestAnimationFrame(function () {
      positionIndicator(root);
      var fp = root.querySelector('[data-ptab-panel].is-active');
      if (fp) checkReadMore(fp);
      updateRailHead(root);
    });
  }

  /* ---------- Init ---------- */

  function initRoot(root) {
    if (root.__ptabInited) return;
    root.__ptabInited = true;

    var layout = root.getAttribute('data-ptab-layout') || 'horizontal';
    if (layout === 'accordion') {
      initAccordion(root);
      return;
    }
    if (layout === 'vertical') {
      initVertical(root);
      return;
    }

    generateTabs(root);
    setupTabArrows(root);
    resizeObserve(root);
    window.requestAnimationFrame(function () {
      positionIndicator(root);
      var firstPanel = root.querySelector('[data-ptab-panel].is-active');
      if (firstPanel) checkReadMore(firstPanel);
    });
  }

  function onClick(e) {
    var accT = e.target.closest ? e.target.closest('[data-ptab-acc-trigger]') : null;
    if (accT) {
      toggleAccordion(accT);
      return;
    }
    var rm = e.target.closest ? e.target.closest('[data-ptab-readmore]') : null;
    if (rm) {
      toggleReadMore(rm);
      return;
    }
    var btn = e.target.closest ? e.target.closest('[data-ptab]') : null;
    if (!btn) return;
    var root = btn.closest('[data-product-tabs]');
    if (!root) return;
    activateTab(root, btn);
  }

  function initAll(scope) {
    var r = scope || document;
    var roots = r.querySelectorAll('[data-product-tabs]');
    for (var i = 0; i < roots.length; i++) {
      initRoot(roots[i]);
    }
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });

  /* Storefront SRA re-init — the product info column re-renders on variant change
     (Section Rendering API; see component-product-info.js), firing
     `product:variant-changed` on document with detail.root = the freshly swapped
     [data-product-info-root]. Re-run initAll on that subtree so any product-tabs /
     product-accordion block inside it rebuilds on the new DOM. The delegated
     click/keydown listeners live on document and survive the swap; the per-root
     __ptabInited guard is irrelevant here because swapped nodes are brand-new. */
  document.addEventListener('product:variant-changed', function (e) {
    initAll((e.detail && e.detail.root) || document);
  });
})();
